import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  CreditCard,
  QrCode,
  Building,
  CheckCircle2,
  X,
  Loader2,
  ExternalLink,
  RefreshCw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const TRIPAY_CHANNELS = [
  { code: "QRIS", label: "QRIS (semua e-wallet & bank)", icon: QrCode },
  { code: "BRIVA", label: "BRI Virtual Account", icon: Building },
  { code: "BCAVA", label: "BCA Virtual Account", icon: Building },
  { code: "BNIVA", label: "BNI Virtual Account", icon: Building },
  { code: "MANDIRIVA", label: "Mandiri Virtual Account", icon: Building },
];

export const PaymentGatewayModal = ({ invoice, provider = "tripay", diskon = 0, onClose, onPaymentSuccess }) => {
  const [channel, setChannel] = useState("QRIS");
  const [loading, setLoading] = useState(false);
  const [checkout, setCheckout] = useState(null);
  const [paidSuccess, setPaidSuccess] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  const isTripay = provider === "tripay";

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const startPolling = (trxId) => {
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/payments/status/${trxId}`, { withCredentials: true });
        if (res.data.paid) {
          clearInterval(pollRef.current);
          setPolling(false);
          setPaidSuccess(true);
          setTimeout(() => {
            if (onPaymentSuccess) onPaymentSuccess(res.data);
            onClose();
          }, 1800);
        }
      } catch (e) {
        // keep polling silently
      }
    }, 4000);
  };

  const checkStatusNow = async () => {
    if (!checkout?.trx_id) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/payments/status/${checkout.trx_id}`, { withCredentials: true });
      if (res.data.paid) {
        if (pollRef.current) clearInterval(pollRef.current);
        setPolling(false);
        setPaidSuccess(true);
        setTimeout(() => {
          if (onPaymentSuccess) onPaymentSuccess(res.data);
          onClose();
        }, 1500);
      } else {
        setError("Pembayaran belum terdeteksi. Silakan selesaikan pembayaran lalu cek lagi.");
        setTimeout(() => setError(""), 4000);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Gagal cek status");
    }
  };

  const handleCharge = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/payments/charge`,
        { tagihan_id: invoice.id || invoice._id, channel, diskon },
        { withCredentials: true }
      );
      setCheckout(res.data);
      startPolling(res.data.trx_id);
      if (res.data.checkout_url && !res.data.qr_url && !res.data.qr_string && !res.data.pay_code) {
        window.open(res.data.checkout_url, "_blank");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal membuat sesi pembayaran");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="payment-gateway-modal">
      <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-800 bg-[#090f1d]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-cyan-500/20 text-cyan-400 flex items-center justify-center">
              <CreditCard className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Payment Gateway — {provider.toUpperCase()}</h3>
              <p className="text-[11px] text-slate-400">{invoice.no_invoice}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-md" data-testid="close-gateway-modal">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Bill summary */}
          <div className="p-3 bg-slate-900/90 border border-slate-800 rounded-xl flex justify-between items-center">
            <div>
              <p className="text-xs text-slate-400">Total Yang Harus Dibayar:</p>
              <p className="text-lg font-black text-cyan-400 font-mono">
                Rp {Number(invoice.total_tagihan).toLocaleString("id-ID")}
              </p>
            </div>
            <div className="text-right">
              <span className="text-[10px] text-slate-400 block">Pelanggan</span>
              <span className="text-xs font-semibold text-slate-200">{invoice.pelanggan_nama}</span>
            </div>
          </div>

          {error && (
            <div className="p-2.5 bg-rose-500/10 border border-rose-500/30 rounded-lg text-[11px] text-rose-300" data-testid="gateway-error">
              {error}
            </div>
          )}

          {paidSuccess ? (
            <div className="py-6 text-center space-y-3" data-testid="gateway-success">
              <div className="w-14 h-14 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mx-auto animate-bounce">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h4 className="text-base font-bold text-emerald-400">Pembayaran Berhasil Diverifikasi!</h4>
              <p className="text-xs text-slate-300">
                Tagihan {invoice.no_invoice} telah lunas dan layanan otomatis aktif.
              </p>
            </div>
          ) : !checkout ? (
            <div className="space-y-3">
              {isTripay && (
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Pilih Kanal Pembayaran:</label>
                  <select
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    data-testid="gateway-channel-select"
                    className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-xs text-white"
                  >
                    {TRIPAY_CHANNELS.map((c) => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}
              {!isTripay && (
                <p className="text-[11px] text-slate-400">
                  Anda akan diarahkan ke halaman pembayaran {provider.toUpperCase()} (QRIS, VA, e-wallet, kartu).
                </p>
              )}
              <button
                type="button"
                onClick={handleCharge}
                disabled={loading}
                data-testid="gateway-charge-btn"
                className="w-full py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-bold text-xs rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Membuat sesi pembayaran...</> : <><CreditCard className="w-4 h-4" /> Buat Pembayaran Online</>}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* QRIS */}
              {checkout.qr_url && (
                <div className="bg-white p-4 rounded-xl text-center">
                  <div className="text-[11px] font-bold text-slate-800 mb-2">SCAN QRIS UNTUK MEMBAYAR</div>
                  <img src={checkout.qr_url} alt="QRIS" className="w-48 h-48 mx-auto object-contain" data-testid="gateway-qr" />
                </div>
              )}
              {/* Pay code / VA */}
              {checkout.pay_code && (
                <div className="bg-slate-900 border border-slate-800 p-4 rounded-xl text-center space-y-1">
                  <p className="text-xs text-slate-400">Nomor Virtual Account / Kode Bayar:</p>
                  <p className="text-xl font-mono font-bold text-cyan-400 tracking-wider" data-testid="gateway-vacode">{checkout.pay_code}</p>
                </div>
              )}
              {/* Redirect (Xendit/Midtrans) */}
              {checkout.checkout_url && (
                <a
                  href={checkout.checkout_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="gateway-checkout-link"
                  className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Buka Halaman Pembayaran
                </a>
              )}

              <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400">
                {polling && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>{polling ? "Menunggu konfirmasi pembayaran..." : "Sesi dibuat"}</span>
              </div>

              <button
                type="button"
                onClick={checkStatusNow}
                data-testid="gateway-check-status-btn"
                className="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs rounded-xl flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" /> Cek Status Pembayaran
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
